import { Divider, ListSubheader, MenuList } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useKeycloak } from '@react-keycloak/web';
import { MainMenuLink } from 'cleveron-web-components';
import { hasAll } from 'cleveron-web-components/utils';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PATH_PERMISSIONS } from '../../constants';

const useStyles = makeStyles(theme => ({
    divider: {
        backgroundColor: theme.palette.primary.contrastText,
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
        opacity: 0.34,
    },
    drawerContent: {
        flex: 99,
        overflowX: 'hidden',
        overflowY: 'auto',
        paddingBottom: ({ collapsible }) => (collapsible ? 80 : 0),
    },
    hideOutline: {
        outline: 'none',
    },
    subHeader: {
        '&:focus': {
            outline: 'none',
        },
        color: theme.palette.primary.contrastText,
        display: 'block',
        height: 34,
        opacity: 0.7,
        overflow: 'hidden',
        textDecoration: 'none',
        textOverflow: 'ellipsis',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
}));

/**
 * A PATH_PERMISSIONS entry navigates somewhere if it has an external href or a local path. An entry
 * with neither is a group heading, which MainMenu renders as a ListSubheader. Deliberately ignores
 * textKey, so giving a link a custom label cannot turn it into a heading.
 *
 * @param {{ href?: string, path?: string, textKey?: string }} entry
 * @returns {boolean}
 */
export function isMenuLink({ href, path }) {
    return !!href || !!path;
}

export function useMenuOptions() {
    const { keycloak } = useKeycloak();

    const groups = useMemo(() => {
        return PATH_PERMISSIONS.reduce((acc, group) => {
            const paths = group.reduce(
                (accc, { href, icon, path, permissions, target, textKey }) => {
                    const show = Object.keys(permissions).reduce((acccc, resourceAccessKey) => {
                        const access = keycloak.resourceAccess?.[resourceAccessKey];
                        const hasAccess = hasAll(access?.roles, permissions[resourceAccessKey]);
                        return hasAccess && (acccc === null ? true : acccc);
                    }, null);
                    if (show) {
                        return [
                            ...accc,
                            {
                                href,
                                icon,
                                path: path ? `/${path}` : undefined,
                                target,
                                textKey,
                            },
                        ];
                    }
                    return accc;
                },
                []
            );

            if (paths.length) {
                return [...acc, paths];
            }

            return acc;
        }, []);
    }, [keycloak]);

    // Headings are not destinations, so they must not be counted as available options nor used as a
    // redirect target.
    const links = useMemo(() => groups.flat().filter(isMenuLink), [groups]);

    // `links` counts every destination, external ones included, because they are all visible menu
    // items. `first` is a redirect target though, so it can only be an entry with a local path.
    return { first: links.find(link => link.path), groups, links };
}

function MainMenu({ closeMenu, id, badges, collapsed, collapsible }) {
    const classes = useStyles({ collapsible });
    const location = useLocation();
    const search = queryString.stringify({ hl: queryString.parse(location.search).hl });
    const { groups } = useMenuOptions();

    return (
        <div className={classes.drawerContent} id={id} key={id}>
            {groups.map((paths, key) => {
                // A group's first entry can be a pathless heading, so fall back to its textKey.
                const groupKey = paths[0].textKey || paths[0].path;
                return [
                    key !== 0 && <Divider className={classes.divider} key={`${groupKey}Divider`} />,
                    <MenuList
                        className={classes.hideOutline}
                        id={`${id}${paths[0].textKey || paths[0].path?.split('/')[1]}List`}
                        key={groupKey}
                    >
                        {paths.map(({ href, icon, path, target, textKey }) => {
                            if (!isMenuLink({ href, path })) {
                                return (
                                    <ListSubheader
                                        className={classes.subHeader}
                                        disableSticky
                                        id={`${id}${textKey}`}
                                        key={textKey}
                                    >
                                        {!collapsed && (
                                            <FormattedMessage id={textKey || 'page.unknown'} />
                                        )}
                                    </ListSubheader>
                                );
                            }
                            const itemPath = path?.split('/')[1] ?? '';
                            const domId = `${id}${textKey || itemPath}`;
                            const linkTextKey = textKey || `page.${itemPath}`;
                            return (
                                <MainMenuLink
                                    BadgeProps={{ invisible: !badges[itemPath] }}
                                    closeMenu={closeMenu}
                                    collapsed={collapsed}
                                    href={href}
                                    icon={icon}
                                    id={domId}
                                    isMatch={location.pathname.split('/')[1] === itemPath}
                                    key={path || href}
                                    target={target}
                                    textKey={linkTextKey}
                                    to={{ pathname: path, search }}
                                />
                            );
                        })}
                    </MenuList>,
                ];
            })}
        </div>
    );
}

const mapStateToProps = ({ releaseNotes }) => {
    return {
        badges: {
            'release-notes': Object.values(releaseNotes)
                .map(category => category.unread)
                .some(Boolean),
        },
    };
};

export default connect(mapStateToProps)(MainMenu);
